import type { UserProfile, UserRole } from '@ergshoot/shared';

export type RoutingUser = {
  role?: UserRole | null;
};

type RoutingInput = {
  user: RoutingUser | null;
  profiles: UserProfile[];
  activeProfile: UserProfile | null;
  sessionActiveRoles: UserRole[] | null;
};

type ReadyRoutingState = {
  status: 'ready' | 'stale-session-role';
  role: UserRole;
  path: string;
  availableRoles: UserRole[];
};

type MissingRoleRoutingState = {
  status: 'missing-role';
  path: '/select-role';
  availableRoles: UserRole[];
};

export type AuthRoutingState = ReadyRoutingState | MissingRoleRoutingState;

export function profileToRole(profile: UserProfile): UserRole {
  switch (profile.profile_type) {
    case 'hq_admin':
      return 'hq_admin';
    case 'agent':
      return 'agent';
    case 'affiliate_member':
      if (profile.member_role === 'owner') return 'affiliate_owner';
      if (profile.member_role === 'coach') return 'affiliate_coach';
      return 'member';
    case 'individual':
      return 'individual';
    default:
      return 'member';
  }
}

export function getRoleDashboardPath(role: UserRole): string {
  switch (role) {
    case 'hq_admin':
      return '/admin';
    case 'agent':
      return '/agent';
    case 'affiliate_owner':
    case 'affiliate_coach':
      return '/affiliate';
    case 'member':
    case 'individual':
      return '/hub';
    case 'purchaser':
      return '/no-access';
    default:
      return '/';
  }
}

export function getProfileDashboardPath(profile: UserProfile): string {
  return getRoleDashboardPath(profileToRole(profile));
}

/**
 * Return gym profiles that belong to the same portal role as the active profile.
 * Switching gyms must not silently switch the user's role or destination.
 */
export function getSwitchableGymProfiles(
  profiles: UserProfile[],
  activeProfile: UserProfile | null
): UserProfile[] {
  if (activeProfile?.profile_type !== 'affiliate_member') return [];

  const activeRole = profileToRole(activeProfile);
  return profiles.filter(
    (profile) =>
      profile.profile_type === 'affiliate_member' &&
      profile.is_active &&
      Boolean(profile.affiliate_id) &&
      profileToRole(profile) === activeRole
  );
}

/** Return active gym profiles where the user has owner permissions. */
export function getOwnedGymProfiles(profiles: UserProfile[]): UserProfile[] {
  return profiles.filter(
    (profile) =>
      profile.profile_type === 'affiliate_member' &&
      profile.member_role === 'owner' &&
      profile.is_active &&
      Boolean(profile.affiliate_id)
  );
}

/** Session-scoped map of last selected profile id per role (e.g. multi-gym owners). */
export const LAST_PROFILE_BY_ROLE_KEY = 'ergshoot_last_profile_by_role';

export function rememberProfileForRole(profile: UserProfile | null | undefined): void {
  if (!profile || profile.is_active === false) return;
  try {
    const role = profileToRole(profile);
    const raw = sessionStorage.getItem(LAST_PROFILE_BY_ROLE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[role] = profile.id;
    sessionStorage.setItem(LAST_PROFILE_BY_ROLE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage failures
  }
}

export function getRememberedProfileId(role: UserRole): string | null {
  try {
    const raw = sessionStorage.getItem(LAST_PROFILE_BY_ROLE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, string>;
    return map[role] ?? null;
  } catch {
    return null;
  }
}

export function clearRememberedProfiles(): void {
  try {
    sessionStorage.removeItem(LAST_PROFILE_BY_ROLE_KEY);
  } catch {
    // ignore
  }
}

export function findProfileForRole(
  profiles: UserProfile[],
  role: UserRole,
  preferredProfile?: UserProfile | null,
  rememberedProfileId?: string | null
): UserProfile | null {
  if (
    preferredProfile &&
    preferredProfile.is_active !== false &&
    profileToRole(preferredProfile) === role &&
    profiles.some((profile) => profile.id === preferredProfile.id)
  ) {
    return preferredProfile;
  }

  if (rememberedProfileId) {
    const remembered = profiles.find(
      (profile) =>
        profile.id === rememberedProfileId &&
        profile.is_active !== false &&
        profileToRole(profile) === role
    );
    if (remembered) return remembered;
  }

  return (
    profiles.find((profile) => profile.is_active !== false && profileToRole(profile) === role) ||
    null
  );
}

export function getAvailableRoles(user: RoutingUser | null, profiles: UserProfile[]): UserRole[] {
  const activeProfiles = profiles.filter((profile) => profile.is_active !== false);
  const roles = new Set<UserRole>();

  for (const profile of activeProfiles) {
    roles.add(profileToRole(profile));
  }

  if (user?.role) {
    roles.add(user.role);
  }

  return Array.from(roles);
}

export function getRouteEligibleRoles(
  availableRoles: UserRole[],
  sessionActiveRoles: UserRole[] | null
): UserRole[] {
  if (!sessionActiveRoles || sessionActiveRoles.length === 0) {
    return availableRoles;
  }

  const validSessionRoles = sessionActiveRoles.filter((role) => availableRoles.includes(role));
  return validSessionRoles.length > 0 ? validSessionRoles : availableRoles;
}

export function getRouteTargetRole(
  allowedRoles: UserRole[],
  routingRole: UserRole,
  eligibleRoles: UserRole[]
): UserRole | null {
  if (allowedRoles.includes(routingRole)) {
    return routingRole;
  }

  return allowedRoles.find((role) => eligibleRoles.includes(role)) || null;
}

function resolveBaseRole(input: RoutingInput, availableRoles: UserRole[]): UserRole | null {
  if (availableRoles.length === 0) {
    return null;
  }

  const activeProfileRole =
    input.activeProfile && input.activeProfile.is_active !== false
      ? profileToRole(input.activeProfile)
      : null;

  if (activeProfileRole && availableRoles.includes(activeProfileRole)) {
    return activeProfileRole;
  }

  if (input.user?.role && availableRoles.includes(input.user.role)) {
    return input.user.role;
  }

  return availableRoles[0] || null;
}

export function resolveAuthRoutingState(input: RoutingInput): AuthRoutingState {
  const availableRoles = getAvailableRoles(input.user, input.profiles);
  const baseRole = resolveBaseRole(input, availableRoles);

  if (!baseRole) {
    return {
      status: 'missing-role',
      availableRoles,
      path: '/select-role',
    };
  }

  const sessionRoles = input.sessionActiveRoles;
  if (!sessionRoles || sessionRoles.length === 0) {
    return {
      status: 'ready',
      role: baseRole,
      path: getRoleDashboardPath(baseRole),
      availableRoles,
    };
  }

  const validSessionRoles = getRouteEligibleRoles(availableRoles, sessionRoles);
  if (sessionRoles.length > 0 && !sessionRoles.some((role) => availableRoles.includes(role))) {
    return {
      status: 'stale-session-role',
      role: baseRole,
      path: getRoleDashboardPath(baseRole),
      availableRoles,
    };
  }

  const role = validSessionRoles.includes(baseRole) ? baseRole : validSessionRoles[0] || baseRole;
  return {
    status: 'ready',
    role,
    path: getRoleDashboardPath(role),
    availableRoles,
  };
}
